import {
  DateRangePicker,
  DateInput,
  DateSegment,
  Dialog,
  Group,
  Label,
  Popover,
  RangeCalendar,
  CalendarGrid,
  CalendarCell,
  Heading,
  Button as AriaButton,
} from 'react-aria-components';
import type { DateRangePickerProps, DateValue } from 'react-aria-components';
import { CalendarDate } from '@internationalized/date';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { DateRange } from '@/contabilidad/hooks/useDateRangeFilter';

interface DateRangeSelectorProps {
  value: DateRange | null;
  onChange: (range: DateRange | null) => void;
  className?: string;
}

function toCalendarDate(d: Date): CalendarDate {
  return new CalendarDate(d.getFullYear(), d.getMonth() + 1, d.getDate());
}

function toJsDate(cd: DateValue): Date {
  return new Date(cd.year, cd.month - 1, cd.day);
}

export function DateRangeSelector({ value, onChange, className }: DateRangeSelectorProps) {
  const pickerValue: { start: CalendarDate; end: CalendarDate } | null = value
    ? { start: toCalendarDate(value.start), end: toCalendarDate(value.end) }
    : null;

  const handleChange: DateRangePickerProps<CalendarDate>['onChange'] = (val) => {
    if (!val) {
      onChange(null);
      return;
    }
    onChange({ start: toJsDate(val.start), end: toJsDate(val.end) });
  };

  return (
    <DateRangePicker
      value={pickerValue}
      onChange={handleChange}
      className={cn('group flex flex-col gap-1', className)}
    >
      <Label className="sr-only">Rango de fechas</Label>
      <Group className="flex items-center gap-1.5 rounded-lg border border-input bg-background px-3 py-1.5 text-sm shadow-sm cursor-pointer hover:border-primary/50 transition-colors focus-within:border-primary focus-within:ring-1 focus-within:ring-primary/30">
        <CalendarIcon className="size-3.5 text-muted-foreground shrink-0" />
        <DateInput slot="start" className="flex items-center gap-0.5">
          {(segment) => (
            <DateSegment
              segment={segment}
              className={cn(
                'inline rounded px-0.5 text-xs tabular-nums outline-none focus:bg-primary focus:text-primary-foreground',
                segment.isPlaceholder && 'text-muted-foreground'
              )}
            />
          )}
        </DateInput>
        <span className="text-xs text-muted-foreground">–</span>
        <DateInput slot="end" className="flex items-center gap-0.5">
          {(segment) => (
            <DateSegment
              segment={segment}
              className={cn(
                'inline rounded px-0.5 text-xs tabular-nums outline-none focus:bg-primary focus:text-primary-foreground',
                segment.isPlaceholder && 'text-muted-foreground'
              )}
            />
          )}
        </DateInput>
        <AriaButton className="ml-1 outline-none">
          <ChevronDown className="size-3.5 text-muted-foreground" />
        </AriaButton>
      </Group>

      <Popover
        className={cn(
          'z-50 rounded-md border bg-popover p-4 text-popover-foreground shadow-md outline-none',
          'data-[entering]:animate-in data-[entering]:fade-in-0 data-[entering]:zoom-in-95',
          'data-[exiting]:animate-out data-[exiting]:fade-out-0 data-[exiting]:zoom-out-95'
        )}
      >
        <Dialog className="outline-none">
          <RangeCalendar className="w-full">
            <header className="flex items-center justify-between pb-4">
              <AriaButton
                slot="previous"
                className="inline-flex size-8 items-center justify-center rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
              >
                <ChevronLeft className="size-4" />
              </AriaButton>
              <Heading className="text-sm font-medium" />
              <AriaButton
                slot="next"
                className="inline-flex size-8 items-center justify-center rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
              >
                <ChevronRight className="size-4" />
              </AriaButton>
            </header>
            <CalendarGrid className="border-collapse space-y-1">
              {(date) => (
                <CalendarCell
                  date={date}
                  className={cn(
                    'relative h-9 w-9 text-center text-sm p-0 font-normal outline-none cursor-pointer rounded-md',
                    'focus:z-10 focus:ring-2 focus:ring-ring focus:ring-offset-2',
                    'data-[today]:bg-accent data-[today]:text-accent-foreground',
                    'data-[selected]:bg-primary data-[selected]:text-primary-foreground',
                    'data-[selection-start]:rounded-l-md data-[selection-end]:rounded-r-md',
                    'data-[outside-month]:text-muted-foreground data-[outside-month]:opacity-50',
                    'data-[disabled]:text-muted-foreground data-[disabled]:opacity-50 data-[disabled]:cursor-not-allowed',
                    'hover:bg-accent hover:text-accent-foreground',
                    'data-[unavailable]:text-destructive data-[unavailable]:line-through data-[unavailable]:cursor-not-allowed'
                  )}
                />
              )}
            </CalendarGrid>
          </RangeCalendar>
        </Dialog>
      </Popover>
    </DateRangePicker>
  );
}
